import React, { useCallback, useMemo, type FC } from 'react';
import type { CouponDetailParams, CouponDetailProps, CouponDetailSection } from './coupon-detail.types';
import { Text } from '../../../atoms/text';
import { FlatList } from '../../../molecules/flat-list';
import styles from './coupon-detail.module.css';

export const CouponDetail: FC<CouponDetailProps> = ({ coupon, content }) => {
    const renderHtmlContent = useCallback((html: string | undefined) => {
        if (!html) return null;

        return (
            <div className={styles.htmlContent}>
                <div dangerouslySetInnerHTML={{ __html: html }} />
            </div>
        );
    }, []);

    const sections: CouponDetailSection[] = useMemo(
        () => [
            {
                title: content?.title,
                description: `${content?.usageNotice}`,
                isHtml: true,
            },
            {
                title: content?.validityPeriod,
                description: `${coupon.startDate} - ${coupon.endDate}`,
                isHtml: false,
            },
            {
                title: content?.devices,
                description: coupon.devices,
                isHtml: false,
            },
            {
                title: content?.payment,
                description: content?.paymentDescription,
                isHtml: true,
            },
            {
                title: content?.shipping,
                description: coupon.shippingAddress,
                isHtml: false,
            },
            {
                title: content?.products,
                description: coupon.productDescription,
                isHtml: true,
            },
        ],
        [content, coupon]
    );

    const renderItem = useCallback(
        ({ item }: CouponDetailParams) => (
            <div className={styles.section}>
                {item.description && (
                    <div>
                        <Text className={styles.sectionTitle}>{item.title}</Text>
                        <div>
                            {item.isHtml ? (
                                renderHtmlContent(item.description)
                            ) : (
                                <Text className={styles.sectionContent}>{item.description}</Text>
                            )}
                        </div>
                    </div>
                )}
            </div>
        ),
        [renderHtmlContent]
    );

    return (
        <div className={styles.container}>
            <FlatList
                contentContainerClassName={styles.flatListContent}
                data={sections}
                renderItem={renderItem}
                keyExtractor={(item: CouponDetailSection, index: number) => item.title + `section-${index}`}
                showsVerticalScrollIndicator={false}
            />
        </div>
    );
};
